// composables/useCatalogFilterTokens.js
import { useState, useNuxtApp } from '#app';
import { useRequest } from '@/composables/useRequest';

const FALLBACK = {
  brands: [],
  transport: [],
  bodytype: [],
  transmission: [],
  fuel: [],
  wheelDrive: [],
  models: [],
  regions: [],
  colors: [],
  dealers: [],
};

const TTL_MS = 5 * 60 * 1000;

// The in-flight promise must NOT live at module scope: on the server that
// scope is shared by every concurrent request in the process, so one user's
// in-flight fetch would get handed back as another user's result without
// ever populating that user's own `tokens` state (leaving it null on
// hydration). Stashing it on the (per-request on the server, per-session on
// the client) Nuxt app instance keeps the dedupe without the cross-request leak.
export function useCatalogFilterTokens() {
  const nuxtApp = useNuxtApp();
  const tokens = useState('catalogFilterTokens', () => null);
  const loaded = useState('catalogFilterTokensLoaded', () => false);
  const loadedAt = useState('catalogFilterTokensLoadedAt', () => 0);

  const reset = () => {
    tokens.value = null;
    loaded.value = false;
    loadedAt.value = 0;
    nuxtApp._catalogFilterTokensPromise = null;
  };
  const normalize = (payload) => {
    if (!payload.success) return FALLBACK;
    const data = payload.data ?? payload;

    return {
      brands: Array.isArray(data.brands)
        ? data.brands
        : Array.isArray(data.tm)
          ? data.tm
          : [],
      transport: Array.isArray(data.transport)
        ? data.transport
        : FALLBACK.transport,
      bodytype: Array.isArray(data.bodytype)
        ? data.bodytype
        : FALLBACK.bodytype,
      transmission: Array.isArray(data.transmission)
        ? data.transmission
        : FALLBACK.transmission,
      fuel: Array.isArray(data.fuel) ? data.fuel : FALLBACK.fuel,
      wheelDrive: Array.isArray(data.wheelDrive)
        ? data.wheelDrive
        : FALLBACK.wheelDrive,
      models: Array.isArray(data.models) ? data.models : FALLBACK.models,
      regions: Array.isArray(data.regions) ? data.regions : FALLBACK.regions,
      colors: Array.isArray(data.colors) ? data.colors : FALLBACK.colors,
      dealers: Array.isArray(data.dealers) ? data.dealers : FALLBACK.dealers,
    };
  };
  const ensureLoaded = async ({ force = false } = {}) => {
    const fresh = Date.now() - loadedAt.value < TTL_MS;

    if (!force && loaded.value && tokens.value && fresh) return tokens.value;
    if (nuxtApp._catalogFilterTokensPromise)
      return nuxtApp._catalogFilterTokensPromise;

    nuxtApp._catalogFilterTokensPromise = (async () => {
      try {
        const res = await useRequest('/catalog/filters');
        tokens.value = normalize(res);
        loaded.value = true;
        loadedAt.value = Date.now();
        return tokens.value;
      } finally {
        nuxtApp._catalogFilterTokensPromise = null;
      }
    })();

    return nuxtApp._catalogFilterTokensPromise;
  };
  return {
    tokens,
    loaded,
    ensureLoaded,
    reset,
  };
}
